import { Flex } from "@chakra-ui/react";
import { useEffect } from "react";

import {
  FOCUS_PLUGIN_NAME,
  WATERMARK_PLUGIN_NAME,
  FRAME_EDITOR_PLUGIN_NAME,
  CROP_MANAGER_PLUGIN_NAME,
  Logger,
  GUIDELINE_PLUGIN_NAME,
  MAGNET_PLUGIN_NAME,
  allInternalPlugins,
  GUIDELINE_SYSTEM_PLUGIN_NAME,
  Dictionary,
} from "@noli/core";
import { SVGUnitTest } from "@noli/svg";
import { NoliNativeBoard } from "@noli/native";
import {
  BoardStore,
  BoardStoresOptions,
  switchLangTo,
  useFlags,
  useBoardStore,
  useIsReady,
} from "@noli/business";

import { ENV, CONTAINER_ID } from "@/utils/constants";
import { themeStore } from "@/stores/theme";
import { loginStore } from "@/modules/login/store";
import Header from "@/modules/editor/Header";
import LeftPanel from "@/modules/editor/panels/LeftPanel";
import BoardPanel from "@/modules/editor/panels/BoardPanel";
import RightPanel from "@/modules/editor/panels/RightPanel";
import Monitors from "@/modules/editor/Monitors";
import WenxinHighLight from "@/modules/editor/components/WenxinHighLight";
import { tasksStore } from "@/stores/tasks";
import GlobalModal from "@/modules/global/components/GlobalModal";
import { loadCache, ProjectContentProcessor } from "@/modules/projects/projectProcessor";
import { WATERMARK_NODES_PLUGIN_NAME } from "@/modules/editor/plugins/watermark";

async function initialize(): Promise<void> {
  // pre settings
  Logger.isDebug = true;
  const isGuest = location.pathname === "/guest";
  loginStore.setGuest(isGuest);
  if (isGuest) {
    switchLangTo("en");
  }
  // check cache
  const { cache, needSave } = await loadCache();
  // initialize board
  const unittest = new SVGUnitTest(NoliNativeBoard, CONTAINER_ID, {
    autoResize: true,
    internalPlugins: [GUIDELINE_SYSTEM_PLUGIN_NAME, WATERMARK_NODES_PLUGIN_NAME].concat(
      allInternalPlugins,
    ),
    excludedPlugins: new Set([
      CROP_MANAGER_PLUGIN_NAME,
      FRAME_EDITOR_PLUGIN_NAME,
      FOCUS_PLUGIN_NAME,
      WATERMARK_PLUGIN_NAME,
      GUIDELINE_PLUGIN_NAME,
      MAGNET_PLUGIN_NAME,
    ]),
    useGlobalClipboard: false,
    backgroundColor: themeStore.styles.boardBg,
    fitContainerOptions: {
      targetFields: cache?.globalTransform,
    },
    bgMode: false,
  });

  console.log("Cache Detected!");
  await unittest.renderGraph(cache.graph, undefined, false);

  const isProduction = ENV === "production";
  const storeOptions: BoardStoresOptions = {
    constantsOpt: {
      env: ENV as BoardStoresOptions["constantsOpt"]["env"],
    },
  };
  if (isProduction) {
    Logger.isDebug = false;
  } else {
    storeOptions.debug = {
      selecting: true,
      selectingDetails: false,
    };
  }
  await useBoardStore(
    unittest.api,
    {
      apiInfo: {},
      groupCode: "",
      modelCodes: [""],
    },
    storeOptions,
  );
  // 理论上已经画板已经初始化好了
  if (needSave) {
    ProjectContentProcessor.saveLocalAndCloudProject();
  }
  // post settings
  const { setGuidelineSystem } = useFlags();
  setGuidelineSystem(true);
  BoardStore.board.keyboardPluginNullable?.setConfig({
    preventDefaultKeys: {
      AltLeft: true,
      MetaLeft: true,
      BracketLeft: true,
      BracketRight: true,
    },
    ctrlPreventDefaultKeys: {
      KeyA: true,
      KeyZ: true,
    },
  });
}

function Editor() {
  function handleGlobalKeydown(e: KeyboardEvent) {
    const keyMap: Dictionary<boolean> = {
      NumpadAdd: true,
      NumpadSubtract: true,
      Minus: true,
      Equal: true,
    };
    const event = e || window.event;
    const ctrlKey = event.ctrlKey || event.metaKey;
    if (ctrlKey && keyMap[event.code]) {
      event.preventDefault();
    }
  }

  function preventKeydownDefault(e: KeyboardEvent) {
    // 禁用 button 空格键触发点击事件
    if (
      e.code === "Space" &&
      ((e.target as Element)?.nodeName === "BUTTON" ||
        (e.composedPath()?.[0] as Element)?.nodeName === "BUTTON")
    ) {
      e.preventDefault();
      return false;
    }
  }

  function preventDefaultWheel(e: WheelEvent) {
    // 禁止左右滑（前进后退
    // if (Math.abs(e.deltaX) > 0) {
    //   e.preventDefault();
    //   return false;
    // }
    // 禁止缩放
    if (e.ctrlKey) {
      if (e.deltaY < 0) {
        e.preventDefault();
        return false;
      }

      if (e.deltaY > 0) {
        e.preventDefault();
        return false;
      }
    }
  }

  function beforeUnloadCallback(e: BeforeUnloadEvent) {
    if (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      e.currentTarget?.location.pathname === "/creator" &&
      tasksStore.tasks.size > 0
    ) {
      e.preventDefault();
      e.returnValue = "离开当前页面将丢失全部任务，确定离开该页面?";
      return "离开当前页面将丢失全部任务，确定离开该页面?";
    }
  }

  // 禁止页面前进后退
  function interceptPopState() {
    history.pushState(null, "", document.URL);
  }

  useEffect(() => {
    initialize().then(() => {
      // const { register } = useSelectHooks();
      // const { setDetailsTab } = useFlags();
      // // 设置默认界面为背景界面
      // setDetailsTab("background");
      // // 设置失焦时进入背景界面
      // register({
      //   key: "background",
      //   before: async (nodes) => {
      //     const { detailsTab, setDetailsTab } = useFlags();
      //     if (nodes.length === 0 && detailsTab !== "background") {
      //       setDetailsTab("background");
      //     }
      //   },
      // });
    });

    //防止页面后退
    history.pushState(null, "", document.URL);
    window.addEventListener("popstate", interceptPopState);

    window.onbeforeunload = beforeUnloadCallback;

    document.querySelector("html")?.classList.add("disable-overscroll");
    document.body.classList.add("disable-overscroll");
    document.addEventListener("keydown", handleGlobalKeydown, { capture: false });
    document.addEventListener("keydown", preventKeydownDefault, { capture: false });

    document.addEventListener("wheel", preventDefaultWheel, { passive: false });

    return () => {
      const isGuest = location.pathname === "/guest";
      if (isGuest) loginStore.setGuest(false);

      document.body.classList.remove("disable-overscroll");
      if (useIsReady()) {
        BoardStore.destroy();
      }

      document.removeEventListener("keydown", handleGlobalKeydown);
      document.removeEventListener("keydown", preventKeydownDefault, { capture: false });
      document.removeEventListener("wheel", preventDefaultWheel);
      window.onbeforeunload = () => null;
      window.removeEventListener("popstate", interceptPopState);
    };
  }, []);

  return (
    <Flex
      h="100vh"
      className="p-editor"
      direction="column"
      userSelect="none"
      overflow="hidden"
      overflowX="auto"
      minW="1090px"
    >
      <Header />
      <Flex w="100%" flex={1} overflow="hidden">
        <LeftPanel />
        <BoardPanel />
        <RightPanel />
      </Flex>
      <Monitors />
      <GlobalModal />
      {/* <Onboarding />
      <DistributeFreeCoinModal />
      <VipUpgradeModal /> */}
      <WenxinHighLight />
    </Flex>
  );
}

export default Editor;
